import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { ROLE_LABELS, formatTL } from "@/lib/api";
import { useState, useEffect, useRef } from "react";
import {
  Users, Truck, Factory, FileText, Building2, FileSignature, LogOut, UserSquare2,
  ReceiptText, ClipboardList, Landmark, Search, X, ChevronDown, Settings2
} from "lucide-react";
import { MENU, DEFAULT_GROUPS } from "./menuConfig";
import { MenuEditor } from "./MenuEditor";

// Turkish-safe fold
const fold = (s) => (s || "")
  .toLocaleLowerCase("tr-TR")
  .replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ç", "c")
  .replaceAll("ğ", "g").replaceAll("ü", "u").replaceAll("ö", "o")
  .replaceAll("â", "a").replaceAll("î", "i").replaceAll("û", "u");

// Group config: icon, label, navigation target
const GROUP_META = {
  suppliers:      { label: "Tedarikçiler", icon: Factory, to: "/suppliers" },
  companies:      { label: "Firmalar", icon: Building2, to: "/companies" },
  drivers:        { label: "Şoförler", icon: Users, to: "/drivers" },
  vehicles:       { label: "Araçlar", icon: Truck, to: "/vehicles" },
  personnel:      { label: "Personel", icon: UserSquare2, to: "/personnel" },
  invoices:       { label: "Tedarikçi Faturaları", icon: FileText, to: "/invoices" },
  sales_invoices: { label: "Kesilen Faturalar", icon: ReceiptText, to: "/sales-invoices" },
  offers:         { label: "Teklifler", icon: ClipboardList, to: "/offers" },
  tenders:        { label: "İhaleler", icon: FileSignature, to: "/tenders" },
  bank_transactions: { label: "Banka Hareketleri", icon: Landmark, to: "/bank-transactions" },
};

const primaryLabel = (grp, r) => {
  switch (grp) {
    case "invoices":       return r.invoice_no + " · " + (r.supplier_or_type || "-");
    case "sales_invoices": return r.invoice_no + " · " + (r.company_name || "-");
    case "vehicles":       return (r.plate || "-") + (r.model ? ` · ${r.model}` : "");
    case "offers":         return (r.offer_no || "Teklif") + " · " + (r.customer_name || "-");
    case "tenders":        return r.title || r.company_name || "İhale";
    case "bank_transactions": return (r.description || "Banka hareketi").slice(0, 50);
    default:               return r.name || "";
  }
};

const secondaryLabel = (grp, r) => {
  switch (grp) {
    case "suppliers":      return [r.category, r.phone, r.plate, r.tax_no].filter(Boolean).join(" · ");
    case "companies":      return [r.contact_person, r.phone, r.tax_office, r.tax_no].filter(Boolean).join(" · ");
    case "drivers":        return [r.phone, r.id_no].filter(Boolean).join(" · ");
    case "vehicles":       return r.brand || "";
    case "personnel":      return [r.role, r.phone].filter(Boolean).join(" · ");
    case "invoices":
    case "sales_invoices": return `${r.date || ""} · ${formatTL(r.amount || 0)}${r.status ? " · " + r.status : ""}`;
    case "offers":         return `${r.date || ""} · ${formatTL(r.total_amount || 0)}`;
    case "tenders":        return `${r.deadline || ""} · ${formatTL(r.bid_amount || 0)} · ${r.status || ""}`;
    case "bank_transactions": return `${r.date || ""} · ${formatTL(r.amount || 0)} · ${r.sender_receiver || ""} · ${r.category || ""}`;
    default:               return "";
  }
};

const MenuLink = ({ m, indent = false, onNav }) => {
  const Icon = m.icon;
  return (
    <NavLink
      to={m.to}
      end={m.to === "/"}
      data-testid={`nav-${m.to.replace("/", "") || "dashboard"}`}
      onClick={onNav}
      className={({ isActive }) =>
        `nav-item flex items-center gap-3 py-2.5 text-sm ${indent ? "pl-8 pr-5" : "px-5"} ${isActive ? "active" : "text-neutral-700"}`
      }
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span>{m.label}</span>
    </NavLink>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [dataResults, setDataResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const [menuCfg, setMenuCfg] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [editorOpen, setEditorOpen] = useState(false);

  const loadMenuCfg = async () => {
    try { const { data } = await api.get("/settings/menu-config"); setMenuCfg(data); }
    catch { setMenuCfg({ groups: [], hidden: [] }); }
  };
  useEffect(() => { loadMenuCfg(); }, []);

  const items = MENU.filter(m => {
    if (user?.role === "admin") return true;
    if (!m.roles.includes(user?.role)) return false;
    const ap = user?.allowed_pages;
    if (Array.isArray(ap) && ap.length > 0) return ap.includes(m.to);
    return true;
  });

  // Effective menu config: saved config or defaults
  const cfg = menuCfg?.groups?.length ? menuCfg : { groups: DEFAULT_GROUPS, hidden: menuCfg?.hidden || [] };
  const hiddenSet = new Set(cfg.hidden || []);
  const visibleItems = items.filter(m => !hiddenSet.has(m.to));
  const groupedMenu = cfg.groups
    .map(g => ({ ...g, menuItems: (g.items || []).map(to => visibleItems.find(m => m.to === to)).filter(Boolean) }))
    .filter(g => g.menuItems.length > 0);
  const inGroupSet = new Set(cfg.groups.flatMap(g => g.items || []));
  const ungroupedItems = visibleItems.filter(m => !inGroupSet.has(m.to));

  // Auto-expand the group containing the active route
  useEffect(() => {
    const active = (cfg.groups || []).find(g => (g.items || []).includes(location.pathname));
    if (active) setOpenGroups(s => (s[active.id] ? s : { ...s, [active.id]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuCfg]);

  const query = fold(q.trim());
  const filteredPages = !query ? items : items.filter(m => {
    const hay = fold(`${m.label} ${m.keywords || ""} ${m.to}`);
    return hay.includes(query);
  });

  // Debounced backend search
  useEffect(() => {
    if (q.trim().length < 2) { setDataResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=5`);
        setDataResults(data);
      } catch { setDataResults(null); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Ctrl/Cmd + K to focus search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === "Escape" && document.activeElement === inputRef.current) {
        setQ(""); setDataResults(null);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goto = (to) => { nav(to); setQ(""); setDataResults(null); inputRef.current?.blur(); };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && filteredPages.length > 0) {
      goto(filteredPages[0].to);
    }
  };

  const totalData = dataResults?.total || 0;

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      <aside className="w-[260px] border-r border-[#E5E5E5] bg-white flex flex-col sticky top-0 h-screen" data-testid="app-sidebar">
        <div className="px-6 py-6 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-3">
            <img src="/keskin-logo.png" alt="Keskin" className="w-11 h-11 object-contain shrink-0" />
            <div>
              <div className="font-heading font-extrabold text-sm tracking-tight leading-none">KESKİNTUR PORTAL</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-1">Yönetim Paneli</div>
            </div>
          </div>
        </div>

        {/* Search box */}
        <div className="px-4 pt-3 pb-2 border-b border-[#F5F5F5]">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
            <input
              ref={inputRef}
              data-testid="nav-search-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ara: sayfa, firma, fatura no..."
              className="w-full pl-8 pr-8 py-2 text-xs bg-[#FAFAFA] border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none rounded"
            />
            {q && (
              <button
                onClick={() => { setQ(""); setDataResults(null); inputRef.current?.focus(); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-neutral-400 hover:text-neutral-800"
                data-testid="nav-search-clear"
              >
                <X className="w-3 h-3" strokeWidth={2.2} />
              </button>
            )}
          </div>
          {q && (
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1.5 num flex items-center gap-2">
              <span>{filteredPages.length} sayfa · {totalData} veri {searching && "…"}</span>
              {filteredPages.length > 0 && <span className="text-[#0020C2]">Enter</span>}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-2" data-testid="nav-menu-list">
          {/* SAYFA sonuçları (arama modunda düz liste) */}
          {filteredPages.length === 0 && q && totalData === 0 ? (
            <div className="px-5 py-6 text-center text-xs text-neutral-500">
              &quot;<span className="font-semibold">{q}</span>&quot; için sonuç yok
            </div>
          ) : q ? (
            <>
              {filteredPages.length > 0 && (
                <div className="px-5 py-1 text-[9px] uppercase tracking-widest text-neutral-400 font-semibold">Sayfalar</div>
              )}
              {filteredPages.map((m) => <MenuLink key={m.to} m={m} onNav={() => { setQ(""); setDataResults(null); }} />)}
            </>
          ) : (
            <>
              {/* Grupsuz sayfalar */}
              {ungroupedItems.map((m) => <MenuLink key={m.to} m={m} onNav={() => { setQ(""); setDataResults(null); }} />)}

              {/* Gruplar */}
              {groupedMenu.map((g) => {
                const open = !!openGroups[g.id];
                const hasActive = g.menuItems.some((m) => m.to === location.pathname);
                return (
                  <div key={g.id} className="mt-0.5">
                    <button
                      data-testid={`nav-group-${g.id}`}
                      onClick={() => setOpenGroups((s) => ({ ...s, [g.id]: !open }))}
                      className={`w-full flex items-center justify-between px-5 py-2.5 text-[11px] uppercase tracking-widest font-bold transition-colors ${hasActive ? "text-[#0020C2]" : "text-neutral-500 hover:text-neutral-900"}`}
                    >
                      <span>{g.label}</span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-[9px] font-semibold text-neutral-400 num">{g.menuItems.length}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} strokeWidth={2} />
                      </span>
                    </button>
                    {open && (
                      <div className="pb-1">
                        {g.menuItems.map((m) => <MenuLink key={m.to} m={m} indent onNav={() => { setQ(""); setDataResults(null); }} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* VERİ sonuçları */}
          {dataResults && totalData > 0 && (
            <>
              <div className="border-t border-[#F5F5F5] mt-2 pt-2" />
              {Object.entries(dataResults.results || {}).map(([grp, rows]) => {
                if (!rows || rows.length === 0) return null;
                const meta = GROUP_META[grp];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div key={grp} className="mb-2">
                    <div className="px-5 py-1 text-[9px] uppercase tracking-widest text-neutral-400 font-semibold flex items-center gap-1.5">
                      <Icon className="w-3 h-3" strokeWidth={2} />
                      {meta.label} · {rows.length}
                    </div>
                    {rows.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => goto(meta.to)}
                        className="w-full text-left px-5 py-1.5 hover:bg-[#F5F5F5] transition-colors group"
                        data-testid={`search-result-${grp}-${r.id}`}
                      >
                        <div className="text-xs font-medium text-neutral-800 truncate">{primaryLabel(grp, r)}</div>
                        {secondaryLabel(grp, r) && (
                          <div className="text-[10px] text-neutral-500 truncate num">{secondaryLabel(grp, r)}</div>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </nav>

        <div className="border-t border-[#E5E5E5] p-4">
          <div className="mb-3">
            <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          {user?.role === "admin" && (
            <button
              data-testid="menu-editor-open-btn"
              onClick={() => setEditorOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all duration-200"
            >
              <Settings2 className="w-3.5 h-3.5" strokeWidth={2} />
              Menü Düzenle
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white transition-all duration-200"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
            Çıkış Yap
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-[1600px] mx-auto p-8">
          {children}
        </div>
      </main>

      {editorOpen && (
        <MenuEditor
          config={menuCfg?.groups?.length ? menuCfg : { groups: [], hidden: menuCfg?.hidden || [] }}
          onClose={() => setEditorOpen(false)}
          onSaved={loadMenuCfg}
        />
      )}
    </div>
  );
}
